/* eslint-disable rxjs-angular/prefer-takeuntil */

import {inject, Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {catchError, switchMap, tap} from 'rxjs/operators';
import {AccountSelectorService} from "./account-selector.service";
import {AccountCacheService} from "./account-cache.service";
import {Account} from "../../domain/model/account.model";
import {AccountStorePort} from "../../domain/ports/account-store.port";
import {AccountSyncService} from "./account-sync.service";
import {ACCOUNT_SESSION_REPOSITORY} from "../../domain/tokens/account-session.token";

@Injectable()
export class AccountStoreService implements AccountStorePort {
  private readonly accountSync = inject(AccountSyncService);
  private readonly accountCache = inject(AccountCacheService);
  private readonly accountSelector = inject(AccountSelectorService);
  private readonly accountSession = inject(ACCOUNT_SESSION_REPOSITORY);

  constructor() {
    this.initializeSession();
  }

  getSelectedAccount$(): Observable<Account | undefined> {
    return this.accountSelector.getSelectedAccount$();
  }

  getSelectedAccount(): Account | undefined {
    return this.accountSelector.getSelected();
  }

  getLastAccount(): Account | undefined {
    return this.accountSelector.getLast();
  }

  saveSelectedAccount(account: Account | undefined): Observable<Account | undefined> {
    return this.accountSession.save(account).pipe(
      tap(savedAccount => this.accountSelector.setSelected(savedAccount)),
      catchError(() => of(undefined))
    );
  }

  getAllAccounts(): Observable<Account[]> {
    const cached = this.accountCache.getAll();

    if (cached.length > 0) {
      return of(cached);
    }

    return this.loadAccountsFromStorage();
  }

  getAccountById(id: string): Observable<Account | undefined> {
    const cached = this.accountCache.get(id);

    if (cached) {
      return of(cached);
    }

    return this.loadAccountsFromStorage().pipe(
      tap(accounts => this.accountCache.populate(accounts)),
      switchMap(() => of(this.accountCache.get(id)))
    );
  }

  saveAccounts(accounts: Account[]): Observable<Account[]> {
    return this.accountSync.saveToStorage(accounts).pipe(
      tap(savedAccounts => this.accountCache.populate(savedAccounts)),
      catchError(() => of([]))
    );
  }

  getAccountsFromServer(): Observable<Account[]> {
    return this.accountSync.syncServerWithStorage().pipe(
      switchMap(mergedAccounts => this.accountSync.saveToStorage(mergedAccounts)),
      tap(savedAccounts => this.accountCache.populate(savedAccounts)),
      tap(() => this.restoreSession()),
      catchError(() => this.loadAccountsFromStorage())
    );
  }

  clearAccounts(): Observable<void> {
    return this.accountSync.clearStorage().pipe(
      tap(() => this.accountCache.clear()),
      tap(() => this.accountSelector.clear()),
      catchError(() => of(undefined))
    );
  }

  clearSelectedAccount(): void {
    this.accountSelector.clear();
    this.accountCache.clear()
  }

  private initializeSession(): void {
    this.accountSession.get().subscribe(account => {
      this.accountSelector.setSelected(account);
    });
  }

  private loadAccountsFromStorage(): Observable<Account[]> {
    return this.accountSync.getFromStorage().pipe(
      tap(accounts => this.accountCache.populate(accounts)),
      catchError(() => of([]))
    );
  }

  private restoreSession(): void {
    this.accountSession.get().subscribe(account => {
      this.accountSelector.setSelected(account);
    });
  }
}
